'use client';
import * as React from 'react';
import { useI18n } from '@/components/i18n/I18nProvider';

type Options = { title?: string; description?: string };
type ConfirmFn = (onConfirm: () => void, opts?: Options) => void;

const Ctx = React.createContext<null | {
  confirm: ConfirmFn;
  setOpen: (v: boolean) => void;
  state: { open: boolean; title?: string; description?: string; onConfirm?: () => void };
}>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<{ open: boolean; title?: string; description?: string; onConfirm?: () => void }>({ open: false });

  const confirm: ConfirmFn = (onConfirm, opts) => {
    setState({ open: true, onConfirm, title: opts?.title, description: opts?.description });
  };
  const setOpen = (open: boolean) => setState(s => ({ ...s, open }));

  return <Ctx.Provider value={{ confirm, setOpen, state }}>{children}</Ctx.Provider>;
}

export function useConfirm() {
  const { t } = useI18n();
  const ctx = React.useContext(Ctx);
  if (!ctx) throw new Error('useConfirm must be used within <ConfirmProvider>');
  const { confirm, setOpen, state } = ctx;

  const Dialog = () => (
    state.open ? (
      <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center">
        <div className="w-full max-w-sm rounded-2xl bg-background border p-4 shadow-xl">
          <h3 className="text-base font-medium">{state.title ?? t('common.confirmQuestion')}</h3>
          {state.description && <p className="mt-1 text-sm text-foreground/70">{state.description}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button className="rounded-lg border px-3 py-1.5 text-sm hover:bg-muted" onClick={() => setOpen(false)}>{t('common.cancel')}</button>
            <button
              className="rounded-lg border px-3 py-1.5 text-sm hover:bg-muted bg-primary/10"
              onClick={() => { state.onConfirm?.(); setOpen(false); }}
            >
              {t('common.confirm')}
            </button>
          </div>
        </div>
      </div>
    ) : null
  );

  return { confirm, Dialog };
}
